import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  SelectHTMLAttributes,
} from 'react';

const PILL_BASE =
  'w-full rounded-pill bg-white text-brand-darkest px-5 py-3 shadow-card outline-none';

// Compact variant for dense clinical / list rows: same brand look, half the
// vertical padding, slightly smaller text. Use anywhere screen height matters.
const COMPACT_BASE =
  'w-full rounded-xl bg-white text-brand-darkest px-3 py-2 text-sm shadow-card outline-none placeholder:text-brand-darkest/40';

// Defaults that suppress browser prefill, autocorrect, spell-check, and
// auto-capitalization on every in-app field. The login / forgot-password
// pages opt back in by passing explicit autoComplete="email" /
// "current-password" props, which override these defaults via the spread.
const NO_PREFILL_DEFAULTS = {
  autoComplete: 'off',
  autoCorrect: 'off',
  spellCheck: false,
  autoCapitalize: 'off',
} as const;

export function CompactInput({
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...NO_PREFILL_DEFAULTS}
      {...rest}
      className={`${COMPACT_BASE} ${className}`}
    />
  );
}

export function PillInput({
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...NO_PREFILL_DEFAULTS}
      {...rest}
      className={`${PILL_BASE} placeholder:text-brand-darkest/40 ${className}`}
    />
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  options: Array<{ value: string; label: string }>;
};

export function PillSelect({ options, className = '', ...rest }: SelectProps) {
  return (
    <select {...rest} className={`${PILL_BASE} appearance-none ${className}`}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function PillButton({
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={`w-full rounded-pill bg-brand-orange text-white font-semibold py-3 disabled:opacity-50 ${className}`}
    />
  );
}
